import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Repeat, Edit, Trash2, Plus, RefreshCw, Loader, CheckSquare, AlertCircle } from 'lucide-react';
import api from '../utils/api';


const RecurringTaskTemplates = ({ user, onEditTask }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchRecurringTemplates();
  }, []);

  const fetchRecurringTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Fetch all tasks marked as recurring templates
      const response = await api.get(`/tasks/recurring-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch recurring templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/tasks/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowDeleteConfirm(null);
      fetchRecurringTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(`Failed to delete template: ${error.response?.data?.detail || error.message}`);
    }
  };

  const formatRecurringRule = (rule) => {
    if (!rule) return 'No recurrence rule';
    
    try {
      const ruleObj = typeof rule === 'string' ? JSON.parse(rule) : rule;
      // Support both old format (freq/DAILY) and new format (frequency/daily)
      const freq = (ruleObj.frequency || ruleObj.freq || 'daily').toLowerCase();
      const interval = ruleObj.interval || 1;
      
      const weekDays = [
        { value: 0, label: 'Sun' },
        { value: 1, label: 'Mon' },
        { value: 2, label: 'Tue' },
        { value: 3, label: 'Wed' },
        { value: 4, label: 'Thu' },
        { value: 5, label: 'Fri' },
        { value: 6, label: 'Sat' }
      ];
      
      if (freq === 'daily') {
        const daysOfWeek = ruleObj.daysOfWeek || [];
        if (daysOfWeek.length === 0) {
          return interval === 1 ? 'Every day' : `Every ${interval} days`;
        } else if (daysOfWeek.length === 5 && [1,2,3,4,5].every(d => daysOfWeek.includes(d))) {
          return 'Weekdays';
        } else {
          const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
          return `Every ${dayNames}`;
        }
      } else if (freq === 'weekly') {
        const daysOfWeek = ruleObj.daysOfWeek || ruleObj.byday || [];
        const dayNames = daysOfWeek.length > 0 
          ? daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ')
          : '';
        if (interval === 1) {
          return dayNames ? `Weekly on ${dayNames}` : 'Weekly';
        }
        return dayNames ? `Every ${interval} weeks on ${dayNames}` : `Every ${interval} weeks`;
      } else if (freq === 'monthly') {
        // Check for quarterly (interval=3)
        if (interval === 3) {
          return 'Quarterly';
        }
        return interval === 1 ? 'Monthly' : `Every ${interval} months`;
      } else if (freq === 'yearly') {
        return interval === 1 ? 'Yearly' : `Every ${interval} years`;
      }
      return freq;
    } catch (error) {
      return 'Invalid rule';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Repeat className="w-6 h-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recurring Task Templates</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage recurring task templates that automatically generate new tasks
                </p>
              </div>
            </div>
            <button
              onClick={fetchRecurringTemplates}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {templates.length === 0 ? (
            <div className="text-center py-12">
              <Repeat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Recurring Task Templates</h3>
              <p className="text-gray-600 mb-6">
                You haven't created any recurring task templates yet. Create a task and mark it as a recurring template to see it here.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto text-left">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How to create recurring task templates:</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-700">
                      <li>Create or edit a task</li>
                      <li>Enable "Recurring Template" in task settings</li>
                      <li>Configure the recurrence rule (daily, weekly, monthly)</li>
                      <li>Save the task</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-6 hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <CheckSquare className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          template.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                          template.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                          template.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {template.priority || 'Normal'}
                        </span>
                      </div>

                      {template.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {template.description}
                        </p>
                      )}

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Repeat className="w-4 h-4" />
                          <span>{formatRecurringRule(template.recurring_rule)}</span>
                        </div>
                        
                        {template.recurring_due_time && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span>Due at: {template.recurring_due_time}</span>
                          </div>
                        )}

                        {template.next_generation_date && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Next generation: {new Date(template.next_generation_date).toLocaleDateString()}
                            </span>
                          </div>
                        )}

                        {template.project_name && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                              Project: {template.project_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={async () => {
                          console.log('🔍 Edit button clicked for template:', template.id);
                          if (onEditTask) {
                            // Fetch full task details and open in edit mode
                            try {
                              const token = localStorage.getItem('access_token');
                              console.log('📡 Fetching task details...');
                              const response = await api.get(`/tasks/${template.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              console.log('✅ Task fetched:', response.data);
                              console.log('🎯 Calling onEditTask...');
                              onEditTask(response.data);
                              console.log('✅ onEditTask called');
                            } catch (error) {
                              console.error('❌ Failed to load task:', error);
                              alert('Failed to load task for editing: ' + (error.response?.data?.detail || error.message));
                            }
                          } else {
                            console.error('❌ onEditTask handler not provided');
                          }
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit template"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(template.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Delete Confirmation */}
                  {showDeleteConfirm === template.id && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 mb-3">
                        Are you sure you want to delete this recurring template? This will not affect tasks already generated from this template.
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecurringTaskTemplates;
